// app/page.tsx
'use client'

import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Zap,
  Target,
  BarChart3,
  RefreshCcw,
  Timer,
  Shield,
  Sparkles,
  CheckCircle2,
  Clock,
} from 'lucide-react'
// ScoreTrend (illustrative student-progress chart) was the hero
// visual through PR #428 with an "Example" pill. Replaced in this
// PR by TutorChatMockup — leads with the AI Tutor (our headline
// differentiator) rather than implying real student outcomes we
// don't have yet. The ScoreTrend file is kept around because the
// chart concept might come back once we have launch data; just
// not as the hero visual pre-launch.
import TutorChatMockup from './TutorChatMockup'
import StatsBanner from '@/components/landing/StatsBanner'
import FeatureHighlights from '@/components/landing/FeatureHighlights'
import ComparisonTable from '@/components/landing/ComparisonTable'
import HowItWorks from '@/components/landing/HowItWorks'
import FAQ from '@/components/landing/FAQ'
import FinalCTA from '@/components/landing/FinalCTA'
import Footer from '@/components/landing/Footer'
import { GUARANTEE_PROMISED_GAIN } from '@/lib/scores/guarantee'

export default function Home() {
  return (
    <main className="min-h-screen bg-background text-foreground">
      {/* soft page glow */}

      {/* ================= HERO (true full-bleed) ================= */}
      {/* HERO – full-bleed band with wide container */}
      <section className="relative w-full overflow-hidden border-b border-border/60 bg-background">
        {/* soft glow */}

        {/* full width band; content centered but wide */}
        <div className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
          <div className="mx-auto w-full max-w-[1320px] grid items-stretch gap-10 md:grid-cols-2">
            {/* Left: copy */}
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5 text-brand-ink" />
                UnlimitedTests: High School Prep
              </div>

              {/* reduce heading weight/size a bit for balance */}
              <h1 className="text-4xl md:text-5xl font-bold leading-tight tracking-tight">
                Digital SAT practice that matches{' '}
                <span className="text-brand-ink">the real test.</span>
              </h1>

              <p className="mt-4 max-w-xl text-base md:text-lg text-muted-foreground">
                The same interface as the real thing, a question bank we check before you see it,
                and drills built from your own mistakes. One subscription. PSAT, ACT and AP are on
                the way.
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild className="bg-brand hover:bg-brand-hover">
                  <Link href="/try">Start Free Diagnostic Test</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="bg-emerald-500 text-white hover:bg-emerald-400 font-semibold"
                >
                  <Link href="/pricing">See Pricing</Link>
                </Button>
              </div>

              {/* Trust line → the score guarantee (roadmap 3.1). */}
              <p className="mt-3 text-sm text-muted-foreground">
                Pro comes with a{' '}
                <Link
                  href="/guarantee"
                  className="font-medium text-brand-ink underline underline-offset-2 hover:text-brand-ink"
                >
                  +{GUARANTEE_PROMISED_GAIN}-point score guarantee
                </Link>{' '}
                — improve, or your next month is free.
              </p>

              {/* Friction-reducer under the primary CTA — mirrors FinalCTA's
                  "No credit card required • Free tier forever" line. */}
              <p className="mt-2.5 text-xs text-muted-foreground">
                ✓ Free forever — no credit card required
              </p>

              <ul className="mt-5 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                <li className="flex items-center gap-2">
                  <Timer className="h-4 w-4 text-brand-ink" /> Real section timers & pacing
                </li>
                <li className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-brand-ink" /> Topic heatmaps & analytics
                </li>
                <li className="flex items-center gap-2">
                  <RefreshCcw className="h-4 w-4 text-pink-300" /> Auto drills from mistakes
                </li>
                <li className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-emerald-700 dark:text-emerald-300" />{' '}
                  Privacy-first, no ads
                </li>
              </ul>
            </div>

            {/* Right: hero mockup of the AI Tutor chat. Leads with
                the product's headline differentiator (context-aware
                AI tutoring) rather than implying real student
                outcomes we don't have data to back yet.
                Previously this header carried a small "Preview" pill
                intended to flag the chat as a UI mockup, but the
                pill's rounded-full + high-contrast cyan styling read
                as a button (and a non-functional one at that). The
                hero is unambiguously a marketing illustration —
                card title + Premium body text already frame this as
                product positioning, not a live conversation — so
                the pill added confusion without adding clarity. */}
            <Card className="border-border/80 bg-card/60 backdrop-blur">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base font-semibold text-foreground">
                  <Sparkles className="h-5 w-5 text-brand-ink" />
                  <span>AI Tutor</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <TutorChatMockup />
                <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
                  Context-aware tutoring built on your practice history — free to try daily, more
                  with Pro.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* =============== STATS BANNER (after hero) =============== */}
      <StatsBanner />

      {/* =============== TESTS INCLUDED (bundle positioning) =============== */}
      <section className="px-5 py-14 md:px-10">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8 text-center">
            <h2 className="text-2xl font-semibold md:text-3xl">What&apos;s included</h2>
            <p className="mt-2 text-sm text-muted-foreground md:text-base">
              The SAT today. PSAT, ACT and AP are on the way.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <TestCard
              name="SAT"
              status="live"
              description="Full Digital SAT format with adaptive Module 2, section timers, and a full question bank."
              href="/sat"
              ctaLabel="Start SAT practice"
            />
            {/* PSAT and ACT are single tests with no subjects to vote
                between, so we don't route them through /vote (that flow
                is for AP subject prioritization). Linking to their
                dedicated coming-soon landing pages instead. */}
            <TestCard
              name="PSAT / NMSQT"
              status="soon"
              description="Same format as the Digital SAT. National Merit qualification prep. Porting our SAT bank."
              href="/psat"
              ctaLabel="Learn more"
            />
            <TestCard
              name="ACT"
              status="soon"
              description="English, Math and Reading, plus optional Science and Writing. Different format from the SAT — we're building it right."
              ctaLabel="Coming soon"
            />
            <TestCard
              name="AP Exams"
              status="soon"
              description="35+ AP subjects. We'll build the most-requested ones first. Pick yours on the vote page."
              href="/ap"
              ctaLabel="See AP roadmap"
            />
          </div>
          {/* Removed centered "Vote for what we build next →" CTA — the
              AP card already routes to /ap (which contains the voting
              flow), and PSAT/ACT cards have their own "Learn more" CTAs
              now. The redundant footer button added clutter without
              new affordance. */}
        </div>
      </section>

      {/* =============== FEATURES (kept readable) =============== */}
      <section className="px-5 py-14 md:px-10">
        <div className="mx-auto max-w-7xl">
          <h2 className="mb-8 text-center text-2xl font-semibold md:text-3xl">
            Why UnlimitedTests?
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Feature
              icon={<Zap className="h-6 w-6 text-brand-ink" />}
              title="Real Simulation"
              text="Timed, sectioned engine that feels like the real SAT."
            />
            <Feature
              icon={<BarChart3 className="h-6 w-6 text-brand-ink" />}
              title="Instant Analytics"
              text="Topic heatmaps and per-question timing to focus your effort."
            />
            <Feature
              icon={<RefreshCcw className="h-6 w-6 text-pink-300" />}
              title="AI Review Drills"
              text="Your mistakes become custom practice sets automatically."
            />
            <Feature
              icon={<Target className="h-6 w-6 text-emerald-700 dark:text-emerald-300" />}
              title="Track Progress"
              text="Milestones, streaks, and growth charts keep you motivated."
            />
          </div>
        </div>
      </section>

      {/* =============== NEW: CONVERSION-FOCUSED SECTIONS =============== */}

      <FeatureHighlights />
      <ComparisonTable />
      <HowItWorks />
      <FAQ />
      <FinalCTA />
      <Footer />
    </main>
  )
}

/* ------------ small components ------------ */

function Feature({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <div className="rounded-xl border border-border bg-card/40 p-5">
      <div className="mb-2 flex items-center gap-2">
        {icon}
        <h3 className="text-[15px] font-semibold text-foreground">{title}</h3>
      </div>
      <p className="text-sm text-muted-foreground">{text}</p>
    </div>
  )
}

function TestCard({
  name,
  status,
  description,
  href,
  ctaLabel,
}: {
  name: string
  status: 'live' | 'soon'
  description: string
  /** Omit to render a non-clickable CTA (e.g. ACT, whose preview is not yet
   *  public — prod users must not be able to navigate there). */
  href?: string
  ctaLabel: string
}) {
  const isLive = status === 'live'
  return (
    <div
      className={`flex flex-col justify-between rounded-xl border p-5 ${
        isLive ? 'border-brand/40 bg-brand/5' : 'border-border bg-card/40'
      }`}
    >
      <div>
        <div
          className={`mb-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
            isLive
              ? 'border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
              : 'border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300'
          }`}
        >
          {isLive ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
          {isLive ? 'Live now' : 'Coming soon'}
        </div>
        <h3 className="text-lg font-semibold text-foreground">{name}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      </div>
      {href ? (
        <Button
          asChild
          variant={isLive ? 'default' : 'outline'}
          className={
            isLive
              ? 'mt-4 bg-brand hover:bg-brand-hover'
              : 'mt-4 border-border bg-card text-foreground hover:bg-muted'
          }
        >
          <Link href={href}>{ctaLabel}</Link>
        </Button>
      ) : (
        <Button
          disabled
          variant="outline"
          className="mt-4 border-border bg-card text-muted-foreground"
        >
          {ctaLabel}
        </Button>
      )}
    </div>
  )
}
